import React from "react";
import SearchBar from "@/components/SearchBar";
import { TID } from "@/lib/testIds";
import { ArrowUpRight, Zap, LineChart, Newspaper, Shield, BrainCircuit } from "lucide-react";

const SUGGESTED = ["AAPL", "TSLA", "NVDA", "MSFT", "GOOGL", "2330.TW", "2317.TW", "3363.TW"];

const FEATURES = [
  { icon: LineChart, title: "深度財務圖表", detail: "近 5 年營收、獲利、EPS、毛利率、現金流視覺化。" },
  { icon: BrainCircuit, title: "AI 綜合評分", detail: "7 個面向 0-100 分，一眼看懂公司體質。" },
  { icon: Newspaper, title: "新聞情緒分析", detail: "最新新聞自動分類、情緒判斷與影響評估。" },
  { icon: Shield, title: "風險三時序拆解", detail: "短期、中期、長期風險逐項展開。" },
];

export default function Hero({ onSelect }) {
  return (
    <section className="relative grid-bg min-h-[calc(100vh-64px)] flex items-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1200px] mx-auto w-full py-14 sm:py-20">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300 text-xs font-mono uppercase tracking-widest mb-6">
            <Zap className="w-3.5 h-3.5" strokeWidth={2} />
            AI-Powered · Realtime Research
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05]">
            <span className="block">用 AI 讀懂</span>
            <span className="block mt-2 bg-gradient-to-r from-blue-400 via-cyan-300 to-emerald-300 bg-clip-text text-transparent">
              任何一支股票
            </span>
          </h1>

          <p className="mt-6 text-sm sm:text-base text-zinc-400 max-w-xl leading-relaxed">
            輸入股票代碼或公司名稱，即刻生成結構完整、易懂客觀的
            <span className="text-white font-semibold"> AI 股票研究報告</span>。
            涵蓋公司基本面、財務體質、競爭對手、風險評估與 AI 綜合評分。
          </p>

          <div className="w-full max-w-2xl mt-9">
            <SearchBar onSelect={onSelect} />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs uppercase tracking-widest text-zinc-500 mr-2 font-mono">熱門</span>
            {SUGGESTED.map((t) => (
              <button
                key={t}
                data-testid={TID.suggestedTickerButton}
                onClick={() => onSelect(t)}
                className="group inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 hover:border-zinc-700 transition-colors font-mono text-xs text-zinc-200"
              >
                {t}
                <ArrowUpRight className="w-3 h-3 text-zinc-500 group-hover:text-blue-400 transition-colors" strokeWidth={2} />
              </button>
            ))}
          </div>
        </div>

        <div className="mt-16 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="card-tactical p-5 sm:p-6">
              <div className="w-9 h-9 rounded-md bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4">
                <f.icon className="w-4.5 h-4.5 text-blue-400" strokeWidth={1.6} />
              </div>
              <div className="text-sm sm:text-base font-semibold text-white">{f.title}</div>
              <div className="mt-1.5 text-xs sm:text-sm text-zinc-400 leading-relaxed">
                {f.detail}
              </div>
            </div>
          ))}
        </div>

        <section className="mt-16 sm:mt-24 max-w-4xl mx-auto space-y-8 text-zinc-300">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">關於 AI Stock Research Dashboard</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              AI Stock Research Dashboard 是一套整合美股與台股的 AI 股票研究平台，
              專為一般投資人、財經內容創作者、研究員設計。使用者只需輸入股票代碼
              （例如 AAPL、TSLA、NVDA、2330.TW）或公司名稱（Apple、特斯拉、台積電），
              系統即刻整合來自 Finnhub、TWSE 臺灣證券交易所、TPEx 證券櫃檯買賣中心、
              FinMind 台股財報資料庫等多家真實 API，並運用 Anthropic Claude Sonnet 4.5
              大型語言模型，產出一份結構完整、視覺清楚、易讀性高的完整股票研究報告。
            </p>
          </div>

          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-3">核心功能一覽</h3>
            <ul className="space-y-2 text-sm sm:text-base leading-relaxed">
              <li>✓ <strong className="text-white">即時股價與 180 日走勢</strong>：美股由 Finnhub 提供、台股由 TWSE / TPEx 官方 API 抓取</li>
              <li>✓ <strong className="text-white">深度財務資料</strong>：過去 4 季實際財報 + 未來 4 季 AI 推估外推（基於 Finnhub YoY ±40% 上限，非分析師 consensus），涵蓋營收、毛利率、淨利、EPS、現金流、資產負債表</li>
              <li>✓ <strong className="text-white">AI 綜合評分</strong>：7 個維度 (成長性 / 獲利能力 / 財務安全性 / 競爭優勢 / 估值合理性 / 新聞情緒 / 長期潛力) 各自 0-100 分</li>
              <li>✓ <strong className="text-white">競爭對手比較表</strong>：自動找出同業主要對手，比較營收、毛利、EPS、本益比、ROE</li>
              <li>✓ <strong className="text-white">新聞情緒分析</strong>：抓取近期新聞並自動標記情緒（正面／中性／負面）與分類（營運／財報／產業／法規／市場情緒）</li>
              <li>✓ <strong className="text-white">AI 對話研究員</strong>：以 Claude Sonnet 4.5 針對特定股票進行專業問答</li>
              <li>✓ <strong className="text-white">響應式設計</strong>：桌機、平板、手機皆可流暢使用</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-3">目前支援的股票</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              <strong className="text-white">美股（Finnhub API）：</strong>包含蘋果 AAPL、輝達 NVDA、特斯拉 TSLA、微軟 MSFT、Alphabet GOOGL、Meta、亞馬遜 AMZN 等所有 US primary listing 股票。
              <br /><br />
              <strong className="text-white">台股（TWSE 上市 + TPEx 上櫃）：</strong>台積電 2330、鴻海 2317、聯發科 2454、台達電 2308、中華電 2412、上詮 3363 等，並支援任意台股代碼即時查詢。
            </p>
          </div>

          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-3">常見問題 FAQ</h3>
            <dl className="space-y-4 text-sm sm:text-base leading-relaxed">
              <div>
                <dt className="font-semibold text-white">Q: 這個平台的資料是即時的嗎？</dt>
                <dd className="text-zinc-400 mt-1">A: 股價、財報、新聞皆為真實 API 抓取，畫面右上方會顯示資料來源 badge（Finnhub / TWSE / TPEx / FinMind）。未來 4 季顯示的是「AI 推估」（基於 Finnhub 最新 YoY 成長率 ±40% 上限的季節性外推），非華爾街分析師 consensus。</dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Q: 平台會給出買賣建議嗎？</dt>
                <dd className="text-zinc-400 mt-1">A: 不會。本平台僅提供研究資訊整理與 AI 分析，不構成任何投資建議。所有 AI 分析報告皆以客觀、避免絕對用語為原則。</dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Q: 支援哪些股票交易所？</dt>
                <dd className="text-zinc-400 mt-1">A: 目前支援 NASDAQ、NYSE 美股，以及台灣證券交易所 (TWSE 上市) 與證券櫃檯買賣中心 (TPEx 上櫃)。</dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Q: 資料多久更新一次？</dt>
                <dd className="text-zinc-400 mt-1">A: 股價與漲跌快取 15 分鐘、新聞快取 6 小時、財務數據與公司簡介快取 24 小時，皆有 refresh 機制。</dd>
              </div>
            </dl>
          </div>
        </section>
      </div>
    </section>
  );
}
